/**
 * DeepAssist popup widget — a framework-neutral, self-contained chat launcher.
 *
 * Renders a floating button in the bottom-right of any suite app. Opening it
 * shows a panel with an "Assistant" tab (the DeepAssist chat, embedded via an
 * iframe pointing at <deepassistUrl>/embed) and — where the host app already
 * had a help/support popup — a "Support" tab with the contact form.
 *
 * The whole widget lives inside a Shadow DOM so it never collides with the
 * host app's CSS (Element Plus / Vuetify / Tailwind all coexist safely).
 *
 * This file is intentionally dependency-free and identical across every app;
 * per-app differences are passed in via initDeepAssistWidget(config).
 *
 *   import { initDeepAssistWidget } from './deepassist-widget.js'
 *   initDeepAssistWidget({
 *     deepassistUrl: import.meta.env.VITE_DEEPASSIST_URL,   // e.g. https://dev-deepassist.ccorex.com
 *     appLabel: 'DeepCost',
 *     support: { apiBase: import.meta.env.VITE_API_BASE_URL, app: 'DeepCost' }, // omit for no Support tab
 *   })
 */

const HOST_ID = 'deepassist-widget-host';
const DEFAULT_TOKEN_KEYS = ['dcx-token', 'app-token', 'access_token', 'token'];
const DEFAULT_ACCENT = '#0097B2';

let state = null; // singleton

function readToken(keys) {
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v && v !== 'undefined' && v !== 'null') return v;
  }
  return null;
}

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return {};
  }
}

function resolveOrgId(token) {
  const direct = localStorage.getItem('dcx-org-id');
  if (direct && direct !== 'undefined') return direct;
  const claims = token ? decodeJwt(token) : {};
  if (claims.organizationId) return String(claims.organizationId);
  if (claims.orgId) return String(claims.orgId);
  try {
    const org = JSON.parse(localStorage.getItem('dcx-organization') || 'null');
    if (org && org.id) return String(org.id);
  } catch { /* ignore */ }
  return null;
}

function svgIcon(name) {
  if (name === 'chat') {
    return '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
  }
  if (name === 'close') {
    return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  }
  if (name === 'sparkle') {
    return '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/></svg>';
  }
  if (name === 'ai') {
    // Larger two-star AI sparkle for the floating button.
    return '<svg viewBox="0 0 24 24" width="27" height="27" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round"><path d="M12 2l1.8 5.4L19 9.2l-5.2 1.8L12 16.4l-1.8-5.4L5 9.2l5.2-1.8z"/><path d="M18.5 13.5l.7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1-2.1-.7 2.1-.7z"/></svg>';
  }
  // life-buoy / support
  return '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
}

// Lighten a hex colour toward white for the FAB's radial highlight.
function lighten(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const mix = (c) => Math.round(c + (255 - c) * 0.4);
  const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `rgb(${r}, ${g}, ${b})`;
}

function styles(accent) {
  return `
:host { all: initial; }
* { box-sizing: border-box; font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }

.fab {
  position: fixed; left: 0; top: 0;
  width: 60px; height: 60px; border-radius: 50%;
  background: radial-gradient(circle at 32% 28%, ${lighten(accent)}, ${accent} 72%);
  color: #fff; border: none; cursor: grab;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 24px rgba(0,0,0,0.30);
  transition: transform .18s ease, box-shadow .18s ease;
  touch-action: none; user-select: none; -webkit-user-select: none;
}
.fab:hover { transform: scale(1.06); box-shadow: 0 10px 28px rgba(0,0,0,0.36); }
.fab.dragging { cursor: grabbing; transition: none; }
.fab.hidden { display: none; }

/* Siri-like animated aura */
.fab::before {
  content: ''; position: absolute; inset: -5px; border-radius: 50%; z-index: -1;
  background: conic-gradient(from 0deg, #0097B2, #22d3ee, #0097B2, #38bdf8, #2dd4bf, #7c4dff, #0097B2);
  filter: blur(7px); opacity: .8;
  animation: da-aura 4.5s linear infinite;
}
.fab::after {
  content: ''; position: absolute; inset: 0; border-radius: 50%;
  animation: da-pulse 2.6s ease-out infinite;
}
.fab svg { position: relative; z-index: 1; animation: da-bob 3.2s ease-in-out infinite; }
@keyframes da-aura { to { transform: rotate(360deg); } }
@keyframes da-pulse {
  0% { box-shadow: 0 0 0 0 rgba(0,151,178,0.45); }
  70% { box-shadow: 0 0 0 16px rgba(0,151,178,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,151,178,0); }
}
@keyframes da-bob { 0%,100% { transform: scale(1); } 50% { transform: scale(1.1); } }
@media (prefers-reduced-motion: reduce) {
  .fab::before, .fab::after, .fab svg { animation: none; }
}

.panel {
  position: fixed; left: 0; top: 0;
  width: min(400px, calc(100vw - 24px));
  height: min(620px, calc(100vh - 40px));
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 18px 50px rgba(0,0,0,0.24), 0 0 0 1px rgba(0,0,0,0.05);
  display: flex; flex-direction: column;
  opacity: 0; transform: translateY(10px) scale(0.98); pointer-events: none;
  transition: opacity .18s ease, transform .2s ease;
}
.panel.open { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }

.head { background: ${accent}; color: #fff; padding: 12px 14px; display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.head .title { font-size: 14px; font-weight: 600; flex: 1; display: flex; align-items: center; gap: 8px; }
.head .logo { width: 26px; height: 26px; border-radius: 7px; background: rgba(255,255,255,0.18); display: flex; align-items: center; justify-content: center; }
.head .x { background: transparent; border: none; color: #fff; cursor: pointer; opacity: .85; display: flex; padding: 4px; border-radius: 6px; }
.head .x:hover { opacity: 1; background: rgba(255,255,255,0.15); }

.tabs { display: flex; background: #f4f5f7; border-bottom: 1px solid #ececef; flex-shrink: 0; }
.tabs.hidden { display: none; }
.tab { flex: 1; padding: 10px 8px; background: transparent; border: none; cursor: pointer; font-size: 13px; font-weight: 600; color: #6b7280; display: flex; align-items: center; justify-content: center; gap: 6px; border-bottom: 2px solid transparent; }
.tab.active { color: ${accent}; border-bottom-color: ${accent}; background: #fff; }

.body { flex: 1; min-height: 0; position: relative; }
.pane { position: absolute; inset: 0; display: none; }
.pane.active { display: block; }
.pane.assistant { display: none; }
.pane.assistant.active { display: block; }

iframe { width: 100%; height: 100%; border: none; display: block; background: #f4f6f8; }

/* Support form */
.support { position: absolute; inset: 0; overflow-y: auto; padding: 16px 18px 20px; }
.support h4 { margin: 0 0 3px; font-size: 15px; color: #1a2332; }
.support .sub { margin: 0 0 14px; font-size: 12px; color: #6b7280; }
.field { margin-bottom: 11px; }
.field label { display: block; font-size: 11px; font-weight: 600; color: #555; margin-bottom: 4px; text-transform: uppercase; letter-spacing: .04em; }
.field .req { color: #e5484d; }
.field input, .field textarea { width: 100%; padding: 8px 11px; border: 1px solid #dcdfe4; border-radius: 8px; font-size: 13px; color: #1a2332; background: #fafafa; outline: none; resize: vertical; }
.field input:focus, .field textarea:focus { border-color: ${accent}; box-shadow: 0 0 0 3px ${accent}22; background: #fff; }
.err { color: #e5484d; font-size: 12px; margin: 0 0 10px; padding: 8px 10px; background: #fff5f5; border-left: 3px solid #e5484d; border-radius: 6px; }
.submit { width: 100%; padding: 10px; background: ${accent}; color: #fff; border: none; border-radius: 9px; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
.submit:disabled { opacity: .65; cursor: not-allowed; }
.spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.45); border-top-color: #fff; border-radius: 50%; animation: sp .7s linear infinite; }
@keyframes sp { to { transform: rotate(360deg); } }
.success { text-align: center; padding: 30px 18px; }
.success .ok { width: 56px; height: 56px; border-radius: 50%; background: ${accent}18; color: ${accent}; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
.success h4 { font-size: 16px; color: #1a2332; margin: 0 0 8px; }
.success p { font-size: 13px; color: #666; line-height: 1.6; margin: 0 0 18px; }
.success button { padding: 9px 24px; background: #f0f0f2; color: #333; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }

@media (max-width: 480px) {
  .panel.open {
    left: 0 !important; top: 0 !important;
    width: 100vw !important; height: 100dvh !important; border-radius: 0;
  }
}
`;
}

function buildSupportPane(root, cfg) {
  const claims = decodeJwt(state.token || '') || {};
  const name = claims.name || claims.fullName || '';
  const email = claims.email || claims.userName || '';

  const pane = document.createElement('div');
  pane.className = 'pane support-pane';
  pane.innerHTML = `
    <div class="support">
      <h4>Contact Support</h4>
      <p class="sub">We'll get back to you as soon as possible.</p>
      <form>
        <div class="field"><label>Name <span class="req">*</span></label><input name="name" type="text" placeholder="Your name" value="${escapeAttr(name)}" /></div>
        <div class="field"><label>Email <span class="req">*</span></label><input name="email" type="email" placeholder="your@email.com" value="${escapeAttr(email)}" /></div>
        <div class="field"><label>Subject <span class="req">*</span></label><input name="subject" type="text" placeholder="Brief summary of the issue" /></div>
        <div class="field"><label>Description <span class="req">*</span></label><textarea name="description" rows="4" placeholder="Describe the issue in detail..."></textarea></div>
        <p class="err" hidden></p>
        <button type="submit" class="submit">Submit Request</button>
      </form>
    </div>`;

  const form = pane.querySelector('form');
  const errEl = pane.querySelector('.err');
  const btn = pane.querySelector('.submit');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.hidden = true;
    const data = Object.fromEntries(new FormData(form).entries());
    if (!data.name || !data.email || !data.subject || !data.description) {
      errEl.textContent = 'Please fill in all required fields.';
      errEl.hidden = false;
      return;
    }
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span> Sending...';
    try {
      const base = String(cfg.support.apiBase || '').replace(/\/$/, '');
      const res = await fetch(`${base}/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(state.token ? { Authorization: `Bearer ${state.token}` } : {}),
        },
        body: JSON.stringify({ ...data, app: cfg.support.app || cfg.appLabel || '' }),
      });
      if (!res.ok) throw new Error(String(res.status));
      pane.querySelector('.support').innerHTML = `
        <div class="success">
          <div class="ok"><svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
          <h4>Request Submitted!</h4>
          <p>Thanks, ${escapeHtml(data.name)}. We've received your request and will respond to <strong>${escapeHtml(data.email)}</strong> shortly.</p>
          <button type="button" class="close-success">Close</button>
        </div>`;
      pane.querySelector('.close-success').addEventListener('click', () => close());
    } catch {
      errEl.textContent = 'Failed to send your request. Please try again.';
      errEl.hidden = false;
      btn.disabled = false;
      btn.innerHTML = 'Submit Request';
    }
  });

  return pane;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }

function postAuthToIframe() {
  if (!state || !state.iframe) return;
  const token = readToken(state.tokenKeys);
  state.token = token;
  if (!token) return;
  try {
    state.iframe.contentWindow?.postMessage(
      { type: 'deepassist:auth', token, orgId: resolveOrgId(token) },
      state.embedOrigin,
    );
  } catch { /* iframe not ready */ }
}

function ensureIframe() {
  if (state.iframe) return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'ARIVU');
  iframe.setAttribute('allow', 'clipboard-write');
  iframe.src = state.embedUrl;
  iframe.addEventListener('load', () => {
    // Fallback in case we missed the iframe's "ready" ping.
    postAuthToIframe();
  });
  state.assistantPane.appendChild(iframe);
  state.iframe = iframe;
}

function onIframeMessage(e) {
  if (!state || !state.iframe) return;
  if (e.source !== state.iframe.contentWindow) return;
  if (e.origin !== state.embedOrigin) return;
  const data = e.data || {};
  if (data.type === 'deepassist:ready') postAuthToIframe();
}

const FAB_SIZE = 60;
const POS_KEY = 'deepassist-widget-pos';

function isMobile() {
  return window.innerWidth <= 480;
}

function clampPos(left, top) {
  const maxLeft = window.innerWidth - FAB_SIZE - 12;
  const maxTop = window.innerHeight - FAB_SIZE - 12;
  return {
    left: Math.min(Math.max(12, left), Math.max(12, maxLeft)),
    top: Math.min(Math.max(12, top), Math.max(12, maxTop)),
  };
}

function defaultPos() {
  return clampPos(window.innerWidth - FAB_SIZE - 24, window.innerHeight - FAB_SIZE - 24);
}

function loadPos() {
  try {
    const raw = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
    if (raw && typeof raw.left === 'number' && typeof raw.top === 'number') {
      return clampPos(raw.left, raw.top);
    }
  } catch { /* ignore */ }
  return defaultPos();
}

function savePos() {
  try { localStorage.setItem(POS_KEY, JSON.stringify(state.pos)); } catch { /* ignore */ }
}

function applyFabPos() {
  state.fab.style.left = `${state.pos.left}px`;
  state.fab.style.top = `${state.pos.top}px`;
}

// Anchor the panel next to the FAB, flipping/clamping to stay fully on-screen.
function positionPanel() {
  const panel = state.panel;
  if (isMobile()) {
    // Mobile fullscreen is handled by CSS; clear any inline positioning.
    panel.style.left = '';
    panel.style.top = '';
    return;
  }
  const gap = 14, margin = 12;
  const pw = panel.offsetWidth || 400;
  const ph = panel.offsetHeight || Math.min(620, window.innerHeight - 40);
  const fabLeft = state.pos.left, fabTop = state.pos.top;
  const fabRight = fabLeft + FAB_SIZE, fabBottom = fabTop + FAB_SIZE;

  let top;
  if (fabTop - gap - ph >= margin) top = fabTop - gap - ph;            // above
  else if (fabBottom + gap + ph <= window.innerHeight - margin) top = fabBottom + gap; // below
  else top = Math.max(margin, window.innerHeight - ph - margin);       // clamp

  let left = fabRight - pw;                                            // right-align to FAB
  left = Math.min(Math.max(margin, left), window.innerWidth - pw - margin);

  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
}

function startDrag(e) {
  if (state.isOpen) return;
  const startX = e.clientX, startY = e.clientY;
  const orig = { ...state.pos };
  let moved = false;
  try { state.fab.setPointerCapture(e.pointerId); } catch { /* ignore */ }

  const move = (ev) => {
    const dx = ev.clientX - startX, dy = ev.clientY - startY;
    if (!moved && Math.hypot(dx, dy) > 4) { moved = true; state.fab.classList.add('dragging'); }
    if (!moved) return;
    state.pos = clampPos(orig.left + dx, orig.top + dy);
    applyFabPos();
  };
  const up = (ev) => {
    state.fab.removeEventListener('pointermove', move);
    state.fab.removeEventListener('pointerup', up);
    state.fab.removeEventListener('pointercancel', up);
    try { state.fab.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
    if (moved) { state.fab.classList.remove('dragging'); savePos(); }
    else toggle(); // treated as a click
  };
  state.fab.addEventListener('pointermove', move);
  state.fab.addEventListener('pointerup', up);
  state.fab.addEventListener('pointercancel', up);
}

function onResize() {
  if (!state) return;
  state.pos = clampPos(state.pos.left, state.pos.top);
  applyFabPos();
  if (state.isOpen) positionPanel();
}

function open() {
  if (!state) return;
  positionPanel();
  state.panel.classList.add('open');
  state.fab.classList.add('hidden');
  state.isOpen = true;
  if (state.activeTab === 'assistant') {
    ensureIframe();
    postAuthToIframe();
  }
}

function close() {
  if (!state) return;
  state.panel.classList.remove('open');
  state.fab.classList.remove('hidden');
  state.isOpen = false;
}

function toggle() { state.isOpen ? close() : open(); }

function switchTab(tab) {
  state.activeTab = tab;
  for (const el of state.tabButtons) el.classList.toggle('active', el.dataset.tab === tab);
  state.assistantPane.classList.toggle('active', tab === 'assistant');
  if (state.supportPane) state.supportPane.classList.toggle('active', tab === 'support');
  if (tab === 'assistant') { ensureIframe(); postAuthToIframe(); }
}

export function initDeepAssistWidget(config) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const deepassistUrl = (config && config.deepassistUrl) || '';
  if (!deepassistUrl) {
    console.warn('[deepassist-widget] no deepassistUrl provided; widget not mounted');
    return;
  }
  if (document.getElementById(HOST_ID)) return; // already mounted

  const accent = (config && config.accent) || DEFAULT_ACCENT;
  const appLabel = (config && config.appLabel) || 'DeepCoreX';
  const tokenKeys = (config && config.tokenKeys) || DEFAULT_TOKEN_KEYS;
  const support = config && config.support && config.support.apiBase ? config.support : null;

  const base = deepassistUrl.replace(/\/$/, '');
  const embedUrl = `${base}/embed`;
  let embedOrigin;
  try { embedOrigin = new URL(base).origin; } catch { embedOrigin = base; }

  const host = document.createElement('div');
  host.id = HOST_ID;
  // Zero-size fixed host that sits above all app content; the fixed children
  // (FAB + panel) escape it visually while inheriting its stacking context.
  host.style.cssText = 'position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 2147483000;';
  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = styles(accent);
  shadow.appendChild(styleEl);

  // Floating button
  const fab = document.createElement('button');
  fab.className = 'fab';
  fab.setAttribute('aria-label', 'Open ARIVU AI');
  fab.setAttribute('title', 'ARIVU AI — drag to move');
  fab.innerHTML = svgIcon('ai');

  // Panel
  const panel = document.createElement('div');
  panel.className = 'panel';
  panel.innerHTML = `
    <div class="head">
      <span class="logo">${svgIcon('sparkle')}</span>
      <span class="title">ARIVU</span>
      <button class="x" aria-label="Close">${svgIcon('close')}</button>
    </div>
    <div class="tabs${support ? '' : ' hidden'}">
      <button class="tab active" data-tab="assistant">${svgIcon('sparkle')} Assistant</button>
      ${support ? `<button class="tab" data-tab="support">${svgIcon('support')} Support</button>` : ''}
    </div>
    <div class="body"></div>`;

  const body = panel.querySelector('.body');
  const assistantPane = document.createElement('div');
  assistantPane.className = 'pane assistant active';
  body.appendChild(assistantPane);

  shadow.appendChild(fab);
  shadow.appendChild(panel);
  document.body.appendChild(host);

  state = {
    host, shadow, fab, panel, assistantPane,
    supportPane: null,
    iframe: null,
    tabButtons: Array.from(panel.querySelectorAll('.tab')),
    activeTab: 'assistant',
    isOpen: false,
    embedUrl, embedOrigin, tokenKeys,
    appLabel,
    token: readToken(tokenKeys),
    pos: null,
  };

  state.pos = loadPos();
  applyFabPos();

  if (support) {
    const sp = buildSupportPane(shadow, { appLabel, support });
    body.appendChild(sp);
    state.supportPane = sp;
  }

  // Wire events — the FAB toggles on click and can be dragged to reposition.
  fab.addEventListener('pointerdown', startDrag);
  panel.querySelector('.x').addEventListener('click', close);
  for (const el of state.tabButtons) el.addEventListener('click', () => switchTab(el.dataset.tab));
  window.addEventListener('message', onIframeMessage);
  window.addEventListener('resize', onResize);

  state._onKey = (ev) => { if (ev.key === 'Escape' && state.isOpen) close(); };
  window.addEventListener('keydown', state._onKey);

  window.__deepAssistWidget = { open, close, destroy: destroyDeepAssistWidget };
}

export function destroyDeepAssistWidget() {
  if (!state) return;
  window.removeEventListener('message', onIframeMessage);
  window.removeEventListener('resize', onResize);
  if (state._onKey) window.removeEventListener('keydown', state._onKey);
  state.host?.remove();
  delete window.__deepAssistWidget;
  state = null;
}

export default initDeepAssistWidget;
